<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="() => { error.title = ''; error.body = '' }"
  />

  <div v-else class="grid grid-cols-11 gap-2">
    <!-- Items Table -->
    <div class="w-full mx-2 col-span-11 md:col-span-8">
      <div class="mt-2">
        <el-table
          :data="order.orderItems"
          stripe
          border
          style="width: 100%"
          v-if="!order.empty()"
          height="400"
          :max-height="800"
          class="card rounded"
          header-cell-class-name="my-header"
        >
          <el-table-column :min-width="40" type="index" label="#" />
          <el-table-column :min-width="90" prop="item.externalID" :label="t('itemCode')">
            <template #default="scope">
              <span>{{ scope.row.item?.externalID }}</span>
            </template>
          </el-table-column>
          <el-table-column :min-width="200" :label="t('itemName')">
            <template #default="scope">
              <div class="truncate">
                <span>{{ scope.row.item.name }}</span>
                <span v-if="scope.row.packInfo?.packID" class="mx-1">
                  {{ ` - ${getPackName(scope.row)}` }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('price')">
            <template #default="scope">
              {{ formatMoney(scope.row.itemSalePriceAmount, order.invoice?.currency) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('salePrice')">
            <template #default="scope">
              {{ formatDinero(scope.row.soldAtPriceDinero) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('quantity')">
            <template #default="scope">
              <div class="flex items-center">
                <div class="px-2 pt-1">
                  {{ scope.row.quantity }}
                </div>
                <el-popover
                  v-if="scope.row.orderItemSerials && scope.row.orderItemSerials.length"
                  placement="bottom-start"
                  :title="t('serialNumbers')"
                  :min-width="200"
                  trigger="hover"
                >
                  <template #default>
                    <p
                      v-for="(serial, i) in scope.row.orderItemSerials"
                      :key="i"
                    >
                      {{ serial.itemSerial.number }}
                    </p>
                  </template>
                  <template #reference>
                    <div class="flex justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        height="16px"
                        width="16px"
                        version="1.1"
                        viewBox="0 0 15.273 15.273"
                        xml:space="preserve"
                      >
                        <g>
                          <path
                            style="fill: #030104"
                            d="M0,1.803h0.897V13.47H0V1.803z M1.446,13.47h0.505V1.803H1.446V13.47z M2.315,13.47h0.504V1.803   H2.315V13.47z M3.482,13.47h1.104V1.803H3.482V13.47z M5.47,13.47h0.552V1.803H5.47V13.47z M6.878,13.47h2.288V1.803H6.878V13.47z    M12.84,13.47h0.752V1.803H12.84V13.47z M14.248,1.803V13.47h1.025V1.803H14.248z M10.084,13.47h0.492V1.803h-0.492V13.47z    M11.281,13.47h0.492V1.803h-0.492V13.47z"
                          />
                        </g>
                      </svg>
                    </div>
                  </template>
                </el-popover>
              </div>
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('total')">
            <template #default="scope">
              {{ formatMoney(scope.row.soldAtPriceAmount * scope.row.quantity, order.invoice?.currency) }}
            </template>
          </el-table-column>
          <el-table-column :min-width="120" :label="t('notes')">
            <template #default="scope">
              {{ scope.row.notes || '' }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- Sidebar -->
    <div class="col-span-11 md:col-span-3 flex flex-col items-center px-3" style="font-size: 14px !important" v-if="order.id">
      <div class="w-full">
        <div class="flex justify-between w-full p-3 card rounded">
          <p>{{ t('customer') }}</p>
          <p class="flex justify-end custom-bold">
            {{ order?.invoice?.customer?.contactDisplayName ?? order?.invoice?.customer?.name ?? t('customer') }}
          </p>
        </div>

        <div class="card rounded p-3 mt-4">
          <div class="flex justify-between p-3 w-full bg-blue-100 rounded">
            <p>{{ t('quoteNumber') }}</p>
            <p class="flex justify-end custom-bold">
              {{ order?.externalID }}
            </p>
          </div>
          <div class="flex justify-between p-3 w-full">
            <p>{{ t('quotedBy') }}</p>
            <p class="flex justify-end custom-bold">
              {{ order?.user?.fullName || order?.user?.name || '' }}
            </p>
          </div>
          <div class="flex justify-between p-3 w-full">
            <p>{{ t('createdAt') }}</p>
            <p class="flex justify-end custom-bold">
              {{ formatDate(order?.createdAt) }}
            </p>
          </div>
          <div v-if="order.validUntil" class="flex justify-between p-3 w-full">
            <p>{{ t('validUntil') }}</p>
            <p class="flex justify-end custom-bold">
              {{ formatDateOnly(order.validUntil) }}
            </p>
          </div>
          <div class="flex justify-between p-3 w-full">
            <p>{{ t('paymentTerms') }}</p>
            <p class="flex justify-end custom-bold">
              {{ t(order.invoice?.paymentType || 'direct') }}
            </p>
          </div>
          <div class="flex justify-between items-center p-3 w-full">
            <p>{{ t('status') }}</p>
            <QuoteStatusBadge :saleOrderID="order.saleOrderID" />
          </div>
          <div v-if="order.invoice?.customer?.phone" class="flex justify-between p-3 w-full">
            <p>{{ t('phone') }}</p>
            <p class="flex justify-end custom-bold" dir="ltr">
              {{ order.invoice.customer.phone }}
            </p>
          </div>
          <div v-if="order.invoice?.notes" class="flex justify-between p-3 w-full">
            <p>{{ t('notes') }}</p>
            <p class="flex justify-end custom-bold">
              {{ order.invoice.notes }}
            </p>
          </div>
        </div>

        <div class="card rounded p-3 mt-4">
          <div v-if="order.invoice?.totalDiscountAmount > 0" class="flex justify-between w-full p-3">
            <p>{{ t('subtotal') }}</p>
            <p class="flex justify-end custom-bold">
              {{ formatMoney(order.invoice?.totalBeforeDiscounts, order.invoice?.currency) }}
            </p>
          </div>
          <div v-if="order.invoice?.totalDiscountAmount > 0" class="flex justify-between w-full p-3">
            <p>{{ t('discount') }}</p>
            <p class="flex justify-end custom-bold">
              -{{ formatMoney(order.invoice?.totalDiscountAmount, order.invoice?.currency) }}
            </p>
          </div>
          <div class="flex justify-between w-full p-3">
            <p>{{ t('total') }}</p>
            <p class="flex justify-end custom-bold">
              {{ formatMoney(order.invoice?.totalDueAmount, order.invoice?.currency) }}
            </p>
          </div>
        </div>

        <div class="card rounded w-full p-3 mt-4">
          <div class="mt-1 flex flex-col items-center w-full gap-1 text-black">
            <horizontal-button
              faIcon="print"
              :title="t('printQuote')"
              @click="printQuote"
            />
          </div>
          <div class="mt-1 flex flex-col items-center w-full gap-1" :class="order.saleOrderID ? 'opacity-50' : 'text-black'">
            <horizontal-button
              faIcon="shopping-cart"
              :title="t('checkout')"
              :disabled="!!order.saleOrderID"
              @click="checkoutFromQuote"
            />
          </div>
          <p v-if="!order.saleOrderID" class="text-xs text-red-500 text-center mt-2">
            {{ t('checkoutNote') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { Route } from '@/router/routes'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import QuoteStatusBadge from '@/views/sales/QuoteStatusBadge.vue'
import { Order } from '@/models/sales/Order'
import { formatMoney, formatDinero } from '@/utils/money'
import { formatPostgresDate } from '@/utils/date'
import { ElMessageBox } from 'element-plus'

export default defineComponent({
  name: 'QuoteView',

  components: {
    LoadingSpinner,
    AlertBadge,
    HorizontalButton,
    QuoteStatusBadge
  },

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          quoteInfo: 'Quote Info',
          quoteNumber: 'Quote #',
          quotedBy: 'Quoted By',
          createdAt: 'Created At',
          validUntil: 'Valid Until',
          paymentTerms: 'Payment Terms',
          customer: 'Customer',
          phone: 'Phone',
          subtotal: 'Subtotal',
          discount: 'Discount',
          total: 'Total',
          printQuote: 'Print Quote',
          checkout: 'Checkout',
          checkoutNote: 'NOTE: sale prices will be the same as in quote',
          itemCode: 'Code',
          itemName: 'Name',
          price: 'Price',
          salePrice: 'Sale Price',
          quantity: 'Qty',
          serialNumbers: 'Serial Numbers',
          notes: 'Notes',
          direct: 'Direct',
          credit_sale: 'Credit Sale',
          layaway: 'Layaway',
          cash_on_delivery: 'Cash on Delivery',
          status: 'Status',
          quoteExpiredTitle: 'Quote Expired',
          quoteExpiredMessage: 'This quote is past its due date. Do you want to continue?',
          confirm: 'Continue',
          cancel: 'Cancel',
          deletedItemsTitle: 'Checkout Not Allowed',
          deletedItemsMessage: 'You cannot checkout because one or more items have been deleted or disabled.'
        },
        ar: {
          quoteInfo: 'معلومات عرض السعر',
          quoteNumber: 'رقم العرض',
          quotedBy: 'بواسطة',
          createdAt: 'تاريخ الانشاء',
          validUntil: 'صالح حتى',
          paymentTerms: 'شروط الدفع',
          customer: 'الزبون',
          phone: 'الهاتف',
          subtotal: 'المجموع الفرعي',
          discount: 'الخصم',
          total: 'المجموع',
          printQuote: 'طباعة عرض السعر',
          checkout: 'اتمام البيع',
          checkoutNote: 'ملاحظة: أسعار البيع ستكون نفسها الموجودة في عرض السعر',
          itemCode: 'الكود',
          itemName: 'الاسم',
          price: 'السعر',
          salePrice: 'سعر البيع',
          quantity: 'الكمية',
          serialNumbers: 'الأرقام التسلسلية',
          notes: 'ملاحظات',
          direct: 'مباشر',
          credit_sale: 'آجل',
          layaway: 'تقسيط',
          cash_on_delivery: 'الدفع عند الاستلام',
          status: 'الحالة',
          quoteExpiredTitle: 'عرض السعر منتهي',
          quoteExpiredMessage: 'هذا العرض تجاوز تاريخ الصلاحية. هل تريد المتابعة؟',
          confirm: 'متابعة',
          cancel: 'إلغاء',
          deletedItemsTitle: 'لا يمكن اتمام البيع',
          deletedItemsMessage: 'لا يمكنك اتمام البيع لأن واحد أو أكثر من المواد تم حذفها أو تعطيلها.'
        }
      }
    })

    return { t, formatMoney, formatDinero }
  },

  data() {
    return {
      order: new Order(),
      loading: false,
      error: { title: '', body: '' }
    }
  },

  async beforeMount() {
    this.loading = true
    try {
      const id = this.$route.params.id as string
      const resp = await this.$http.get<Order>(`/sales/quotes/${id}`)
      this.order = Order.from(resp)
    } catch (err: any) {
      this.error.title = err.title || 'Error'
      this.error.body = err.body || ''
    } finally {
      this.loading = false
    }
  },

  methods: {
    formatDate(date: string | Date | undefined): string {
      if (!date) return ''
      return formatPostgresDate(String(new Date(date as string)), 'DD/MM/YYYY hh:mm A')
    },
    formatDateOnly(date: string | Date | undefined): string {
      if (!date) return ''
      return formatPostgresDate(String(new Date(date as string)), 'DD/MM/YYYY')
    },

    getPackName(orderItem: any): string {
      if (orderItem.packInfo?.itemPack?.pack?.name) {
        return orderItem.packInfo.itemPack.pack.name
      }
      return ''
    },

    printQuote() {
      const id = this.$route.params.id as string
      this.$router.push(Route.SALES_QUOTES_RECEIPT.replace(':id', id))
    },

    async checkoutFromQuote() {
      if (this.order.saleOrderID) return
      const id = this.$route.params.id as string

      const hasDeletedItems = this.order.orderItems.some(
        (oi) => oi.item?.disabled || oi.item?.deletedAt
      )
      if (hasDeletedItems) {
        ElMessageBox.alert(this.t('deletedItemsMessage'), this.t('deletedItemsTitle'), {
          type: 'error'
        })
        return
      }

      if (this.order.validUntil && new Date(this.order.validUntil) < new Date()) {
        try {
          await ElMessageBox.confirm(
            this.t('quoteExpiredMessage'),
            this.t('quoteExpiredTitle'),
            {
              confirmButtonText: this.t('confirm'),
              cancelButtonText: this.t('cancel'),
              type: 'warning'
            }
          )
        } catch {
          return
        }
      }

      this.$router.push(`${Route.SALES_NEW_SALE}?quoteId=${id}`)
    }
  }
})
</script>

<style>
.custom-bold {
  font-weight: bolder;
}
</style>
